import {
  MediaUnit,
  PriceModel,
  Study,
  VolumePrice2Model,
  VolumePriceModel,
} from '../api/hscan/hospital';

import { Heap } from './heap';

export const isVolumePriceModel = (
  price: PriceModel,
): price is VolumePriceModel | VolumePrice2Model => {
  return price.type === 'VOLUME' || price.type === 'VOLUME2';
};

export const isExpectablePriceModel = (price: PriceModel): boolean => {
  return (
    (price.type === 'VOLUME' || price.type === 'VOLUME2') &&
    price.useExpectedPrice
  );
};

export const calculatePrice = (
  priceModel: PriceModel,
  studies: Study[],
): number => {
  const payableStudies =
    studies?.filter(study => study.status !== 'FAILED') ?? [];
  const { payableStudiesCount: studyCount, payableImageCount: imageCount } =
    getPayableCount(payableStudies);

  switch (priceModel.type) {
    case 'SIMPLE':
      return studyCount > 0 ? priceModel.amount : 0;
    case 'STUDY':
      return studyCount * priceModel.amount;
    case 'IMAGE':
      return Math.ceil(imageCount / priceModel.per) * priceModel.amount;
    case 'VOLUME':
      return studyCount > 0
        ? Math.max(...priceModel.amounts.map(({ amount }) => amount))
        : 0;
    case 'VOLUME2':
      return calculateVolume2Price(priceModel, payableStudies);
  }
};

export const calculateTaxFreePrice = (
  priceModel: PriceModel,
  studies: Study[],
): number => {
  const payableStudies =
    studies?.filter(study => study.status !== 'FAILED') ?? [];
  const { payableStudiesCount: studyCount, payableImageCount: imageCount } =
    getPayableCount(payableStudies);

  switch (priceModel.type) {
    case 'SIMPLE':
      return studyCount > 0 ? priceModel?.taxFree ?? 0 : 0;
    case 'STUDY':
      return studyCount * (priceModel?.taxFree ?? 0);
    case 'IMAGE':
      return (
        Math.ceil(imageCount / priceModel.per) * (priceModel?.taxFree ?? 0)
      );
    case 'VOLUME':
      return studyCount > 0
        ? Math.max(...priceModel.amounts.map(({ taxFree }) => taxFree ?? 0))
        : 0;
    case 'VOLUME2':
      return calculateVolume2TaxFreePrice(priceModel, payableStudies);
  }
};

const getPayableCount = (studies: Study[]) => {
  const payableStudiesCount =
    studies?.filter(study => study.status !== 'FAILED').length ?? 0;
  const payableImageCount =
    studies
      ?.filter(study => study.status !== 'FAILED')
      .map(study => study.numImages)
      .reduce(
        (numImagesSum, studyNumImage) => numImagesSum + studyNumImage,
        0,
      ) ?? 0;
  return {
    payableStudiesCount,
    payableImageCount,
  };
};

export const calculateVolume2PriceAndTaxFreePrice = (
  priceModel: VolumePrice2Model,
  studies: Study[],
): [number, number] => {
  const { units, modalities, defaultOption } = priceModel;

  const virtualTotal = studies.reduce((sum, study) => {
    const modality = study.modalities[0];
    const { virtualSize } = modalities[modality] ?? defaultOption;
    return sum + virtualSize;
  }, 0);
  // 용량 총합

  const visited: Record<number, boolean> = {};
  const heap = new Heap<[number, number, number]>(([a], [b]) => a - b);
  heap.push([0, 0, 0]);
  let answer: [number, number, number] = [Infinity, Infinity, 0];

  while (heap.size() > 0) {
    const top = heap.pop() as [number, number, number];
    const [price, taxFree, volume] = top;

    if (volume >= virtualTotal) {
      if (top < answer) {
        answer = top;
      }
      continue;
    }

    if (visited[volume]) {
      continue;
    }

    units.forEach(
      ({
        virtualVolume: unitVolume,
        price: unitPrice,
        taxFree: unitTaxFree = 0,
      }) => {
        heap.push([
          price + unitPrice,
          taxFree + unitTaxFree,
          volume + unitVolume,
        ]);
      },
    );
  }

  return [answer[0], answer[1]];
};
export const calculateVolume2Price = (
  priceModel: VolumePrice2Model,
  studies: Study[],
) => calculateVolume2PriceAndTaxFreePrice(priceModel, studies)[0];
export const calculateVolume2TaxFreePrice = (
  priceModel: VolumePrice2Model,
  studies: Study[],
) => calculateVolume2PriceAndTaxFreePrice(priceModel, studies)[1];

export const calculateVolumePriceWithFileSize = (
  priceModel: VolumePriceModel | VolumePrice2Model,
  fileSize: number,
): number => {
  if (priceModel.type === 'VOLUME') {
    return calculateVolumePriceWithFileSizeVolumePriceModel(
      priceModel,
      fileSize,
    );
  } else {
    return calculateVolumePriceWithFileSizeVolume2PriceModel(
      priceModel,
      fileSize,
    );
  }
};

const calculateVolumePriceWithFileSizeVolumePriceModel = (
  priceModel: VolumePriceModel,
  fileSize: number,
): number => {
  const matchedRange = priceModel.amounts.find(range =>
    range.upper !== undefined
      ? range.lower <= fileSize && fileSize < range.upper
      : range.lower <= fileSize,
  );
  if (matchedRange === undefined) {
    throw Error('range not found');
  }
  return matchedRange.amount;
};

const calculateVolumePriceAndTaxFreePriceWithFileSizeVolume2PriceModel = (
  priceModel: VolumePrice2Model,
  fileSize: number,
): [number, number] => {
  const { units } = priceModel;
  const sorted = [...units].sort((a, b) => a.volume - b.volume);

  const visited: Record<number, boolean> = {};

  type Entry = [number, number, number, number[]];
  const compareFn = (e1: Entry, e2: Entry) => {
    // eslint-disable-next-line prettier/prettier
    return e1[0] < e2[0] ? -1 : e1[0] === e2[0] ? (e1[1] < e2[1] ? -1 : e1[1] === e2[1] ? 0 : 1) : 1;
  };
  /**
   * [price, taxFree, volume, used unit index[]]
   */
  const heap = new Heap<Entry>(compareFn);
  heap.push([0, 0, 0, []]);
  let answer: Entry = [Infinity, Infinity, 0, []];

  while (heap.size() > 0) {
    const top = heap.pop() as Entry;
    const [price, taxFree, volume, used] = top;

    if (volume >= fileSize) {
      if (compareFn(top, answer) < 0) {
        answer = top;
      }
      continue;
    }
    if (visited[volume]) {
      continue;
    }
    visited[volume] = true;

    sorted.forEach(
      (
        { price: unitPrice, taxFree: unitTaxFree, volume: unitVolume },
        index,
      ) => {
        heap.push([
          price + unitPrice,
          taxFree + (unitTaxFree ?? 0),
          volume + unitVolume,
          used.concat(index),
        ]);
      },
    );
  }

  if (answer[0] === Infinity) {
    throw Error('계산 실패');
  }
  return [answer[0], answer[1]];
};
export const calculateVolumePriceWithFileSizeVolume2PriceModel = (
  priceModel: VolumePrice2Model,
  fileSize: number,
) =>
  calculateVolumePriceAndTaxFreePriceWithFileSizeVolume2PriceModel(
    priceModel,
    fileSize,
  )[0];
export const calculateVolumeTaxFreePriceWithFileSizeVolume2PriceModel = (
  priceModel: VolumePrice2Model,
  fileSize: number,
) =>
  calculateVolumePriceAndTaxFreePriceWithFileSizeVolume2PriceModel(
    priceModel,
    fileSize,
  )[1];

export const calculateVolumeTaxFreePriceWithFileSize = (
  priceModel: VolumePriceModel | VolumePrice2Model,
  fileSize: number,
): number => {
  if (priceModel.type === 'VOLUME') {
    return calculateVolumeTaxFreePriceWithFileSizeVolumePriceModel(
      priceModel,
      fileSize,
    );
  } else {
    return calculateVolumeTaxFreePriceWithFileSizeVolume2PriceModel(
      priceModel,
      fileSize,
    );
  }
};
const calculateVolumeTaxFreePriceWithFileSizeVolumePriceModel = (
  priceModel: VolumePriceModel,
  fileSize: number,
): number => {
  const matchedRange = priceModel.amounts.find(range =>
    range.upper !== undefined
      ? range.lower <= fileSize && fileSize < range.upper
      : range.lower <= fileSize,
  );
  if (matchedRange === undefined) {
    throw Error('range not found');
  }
  return matchedRange.taxFree ?? 0;
};

const GCD = (a: number, b: number): number => (b === 0 ? a : GCD(b, a % b));
const LCM = (a: number, b: number) => (a * b) / GCD(a, b);
// TODO: 막 짬... 20 / 10 짜리 unit 이 있고 size 4가 있으면?
// 지금은 10 짜리 2개에 5개 담을 수 있다고 나오는데, 20짜리 1개에 5개라고 나와야할 것 같음
export const calculateMediaUnitCapacity = (
  contentSize: number,
  units: MediaUnit[],
): { unit: MediaUnit; unitNumber: number; contentNumber: number } => {
  const sorted = [...units].sort((a, b) => a.virtualVolume - b.virtualVolume);

  const unit = sorted.find(
    sortedUnit => sortedUnit.virtualVolume >= contentSize,
  );
  if (!unit) {
    throw Error('no media to handle it');
  }

  const lcm = LCM(unit.virtualVolume, contentSize);

  return {
    unit,
    unitNumber: lcm / unit.virtualVolume,
    contentNumber: lcm / contentSize,
  };
};
